import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  TextField,
  Alert,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Save,
  X,
  FileText,
  User,
  DollarSign,
  Package,
  Clock,
  Target,
  ArrowRight,
} from 'lucide-react';
import CustomButton from '../../components/ui/CustomButton';
import { certificatesService } from '../../services/certificates';

const DraftToProductionModal = ({
  open,
  onClose,
  certificate,
  onConvert,
  isDarkMode,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [issuanceDate, setIssuanceDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [validationSuccess, setValidationSuccess] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const steps = ['Validation', 'Date Selection', 'Review & Convert'];

  // Initialize dates and validate when dialog opens
  useEffect(() => {
    if (open && certificate) {
      const suggestedDates = certificatesService.generateSuggestedDates();
      setIssuanceDate(suggestedDates.issuanceDate);
      setExpiryDate(suggestedDates.expiryDate);
      setCurrentStep(0);
      validateCertificate();
    }
  }, [open, certificate]);

  // Validate dates when they change
  useEffect(() => {
    if (issuanceDate && expiryDate) {
      validateDates();
    }
  }, [issuanceDate, expiryDate]);

  const validateCertificate = async () => {
    if (!certificate) return;

    setIsValidating(true);
    try {
      const validation = await certificatesService.validateDraftConversion(
        certificate.Id
      );
      setValidationErrors(validation.errors || []);

      // Enhanced validation checks
      const warnings = [];
      const success = [];

      // Check completeness
      if (certificate.ImporterName)
        success.push('Importer information is complete');
      if (certificate.ExporterName)
        success.push('Exporter information is complete');
      if (certificate.InvoiceNumber)
        success.push('Invoice information is present');

      if (certificate.InvoiceValueFeeBasis)
        success.push('Invoice Value (Fee Basis) is present');

      if (certificate.Items && certificate.Items.length > 0) {
        success.push(`${certificate.Items.length} certificate item(s) defined`);
      }

      // Check for warnings
      if (!certificate.Remarks) {
        warnings.push('Consider adding remarks for better documentation');
      }
      if (!certificate.ContainerSealNumber) {
        warnings.push('Container seal number is recommended for tracking');
      }

      if (!certificate.PointOfEntry) {
        warnings.push('Point of entry should be specified');
      }

      setValidationWarnings(warnings);
      setValidationSuccess(success);

      // Auto-advance if no errors
      if ((validation.errors || []).length === 0) {
        setTimeout(() => {
          if (currentStep === 0) {
            setCurrentStep(1);
          }
        }, 1000);
      }
    } catch (error) {
      // Robust error handling
      const errorMessage =
        error?.message || error?.toString() || 'Unknown validation error';
      setValidationErrors(['Failed to validate certificate: ' + errorMessage]);
      console.error('Certificate validation error:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const validateDates = () => {
    const errors = [];
    const today = new Date();

    const issuance = new Date(issuanceDate);
    const expiry = new Date(expiryDate);

    // Validate issuance is not in future

    // Expiry must be after issuance
    if (expiry <= issuance) {
      errors.push('Expiry date must be after issuance date');
    }

    const daysDiff = Math.ceil((expiry - issuance) / (1000 * 60 * 60 * 24));

    if (daysDiff < 30) {
      errors.push('Certificate validity period should be at least 30 days');
    }

    // Update validation state
    if (errors.length > 0) {
      setValidationErrors(prev => [
        ...prev.filter(e => !e.includes('date') && !e.includes('validity')),
        ...errors,
      ]);
    } else {
      setValidationErrors(prev =>
        prev.filter(e => !e.includes('date') && !e.includes('validity'))
      );
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleConvert = async () => {
    if (!issuanceDate || !expiryDate) {
      setValidationErrors(['Both issuance and expiry dates are required']);
      return;
    }

    if (validationErrors.length > 0) {
      return;
    }

    setIsConverting(true);
    try {
      await onConvert(issuanceDate, expiryDate);
    } catch (error) {
      // Robust error handling
      const errorMessage =
        error?.message || error?.toString() || 'Unknown conversion error';
      setValidationErrors(['Conversion failed: ' + errorMessage]);
      console.error('Certificate conversion error:', error);
    } finally {
      setIsConverting(false);
    }
  };

  const handleClose = () => {
    if (!isConverting) {
      setCurrentStep(0);
      setIssuanceDate('');
      setExpiryDate('');
      setValidationErrors([]);
      setValidationWarnings([]);
      setValidationSuccess([]);
      setShowAdvanced(false);
      onClose();
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return validationErrors.length === 0;
      case 1:
        return issuanceDate && expiryDate && validationErrors.length === 0;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const formatDate = dateString => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const calculateValidityPeriod = () => {
    if (!issuanceDate || !expiryDate) return '';
    const days = Math.ceil(
      (new Date(expiryDate) - new Date(issuanceDate)) / (1000 * 60 * 60 * 24)
    );
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    const remainingDays = days % 30;

    let period = '';
    if (years > 0) period += `${years} year${years > 1 ? 's' : ''} `;
    if (months > 0) period += `${months} month${months > 1 ? 's' : ''} `;
    if (remainingDays > 0)
      period += `${remainingDays} day${remainingDays > 1 ? 's' : ''}`;

    return period.trim() || `${days} days`;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <RefreshCw size={24} color="#10b981" />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
              }}
            >
              Convert Draft to Production
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDarkMode ? '#94a3b8' : '#64748b',
                mt: 0.5,
              }}
            >
              Certificate:{' '}
              {certificate?.CocNumber || `Draft-${certificate?.Id}`}
            </Typography>
          </Box>
        </Box>

        {/* Progress Stepper */}
        <Box sx={{ mt: 3 }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      color: isDarkMode ? '#e2e8f0' : '#475569',
                      '&.Mui-active': {
                        color: '#10b981',
                        fontWeight: 600,
                      },
                      '&.Mui-completed': {
                        color: '#10b981',
                      },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Step 1: Validation */}
        {currentStep === 0 && (
          <Box>
            {isValidating && (
              <Box sx={{ mb: 3 }}>
                <LinearProgress
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #10b981, #059669)',
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    mt: 1,
                    textAlign: 'center',
                  }}
                >
                  Validating certificate data...
                </Typography>
              </Box>
            )}

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  ❌ The following issues must be resolved:
                </Typography>
                <List dense>
                  {validationErrors.map((error, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <AlertTriangle size={16} color="#ef4444" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">{error}</Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {/* Validation Success */}
            {validationSuccess.length > 0 && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  ✅ Certificate validation successful:
                </Typography>
                <List dense>
                  {validationSuccess.map((success, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <CheckCircle size={16} color="#10b981" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">{success}</Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {/* Validation Warnings */}
            {validationWarnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  ⚠️ Recommendations (optional):
                </Typography>
                <List dense>
                  {validationWarnings.map((warning, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <AlertTriangle size={16} color="#f59e0b" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">{warning}</Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {/* Certificate Summary */}
            <Card
              sx={{
                mb: 3,
                background: isDarkMode
                  ? 'rgba(30, 41, 59, 0.6)'
                  : 'rgba(255, 255, 255, 0.8)',
                border: isDarkMode
                  ? '1px solid rgba(148, 163, 184, 0.1)'
                  : '1px solid rgba(148, 163, 184, 0.2)',
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    mb: 2,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FileText size={20} color="#3b82f6" />
                  Certificate Overview
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <User
                        size={16}
                        color={isDarkMode ? '#94a3b8' : '#64748b'}
                      />
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: 500 }}
                      >
                        Importer: {certificate?.ImporterName || 'Not specified'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <DollarSign
                        size={16}
                        color={isDarkMode ? '#94a3b8' : '#64748b'}
                      />
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: 500 }}
                      >
                        Invoice:{' '}
                        {certificate?.InvoiceValue
                          ? `${certificate.InvoiceValue} ${certificate.InvoiceCurrency}`
                          : 'Not specified'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Package
                        size={16}
                        color={isDarkMode ? '#94a3b8' : '#64748b'}
                      />
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: 500 }}
                      >
                        Items: {certificate?.Items?.length || 0} items
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Clock
                        size={16}
                        color={isDarkMode ? '#94a3b8' : '#64748b'}
                      />
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: 500 }}
                      >
                        Created: {formatDate(certificate?.CreatedAt)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Step 2: Date Selection */}
        {currentStep === 1 && (
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Calendar size={20} color="#3b82f6" />
              Set Certificate Validity Period
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Important:</strong> Once converted to production, these
                dates cannot be changed. The certificate will be officially
                active from the issuance date.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              {/* Issuance Date */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Issuance Date *"
                  type="date"
                  fullWidth
                  value={
                    issuanceDate
                      ? new Date(issuanceDate).toISOString().split('T')[0]
                      : ''
                  }
                  onChange={e => {
                    const selectedDate = e.target.value; // format: YYYY-MM-DD
                    const currentTime = new Date(issuanceDate || new Date())
                      .toISOString()
                      .split('T')[1]; // preserve time
                    const combined = new Date(`${selectedDate}T${currentTime}`);
                    setIssuanceDate(combined.toISOString());
                  }}
                  InputLabelProps={{ shrink: true }}
                  disabled={isConverting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: isDarkMode
                        ? 'rgba(30, 41, 59, 0.5)'
                        : 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    display: 'block',
                    mt: 1,
                  }}
                >
                  When the certificate becomes officially valid
                </Typography>
              </Grid>

              {/* Expiry Date */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Expiry Date *"
                  type="date"
                  fullWidth
                  value={
                    expiryDate
                      ? new Date(expiryDate).toISOString().split('T')[0]
                      : ''
                  }
                  onChange={e => {
                    const selectedDate = e.target.value;
                    const currentTime = new Date(expiryDate || new Date())
                      .toISOString()
                      .split('T')[1];
                    const combined = new Date(`${selectedDate}T${currentTime}`);
                    setExpiryDate(combined.toISOString());
                  }}
                  InputLabelProps={{ shrink: true }}
                  disabled={isConverting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: isDarkMode
                        ? 'rgba(30, 41, 59, 0.5)'
                        : 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    display: 'block',
                    mt: 1,
                  }}
                >
                  When the certificate expires and becomes invalid
                </Typography>
              </Grid>
            </Grid>

            {/* Validity Period Display */}
            {issuanceDate && expiryDate && (
              <Card
                sx={{
                  mt: 3,
                  background: isDarkMode
                    ? 'rgba(59, 130, 246, 0.1)'
                    : 'rgba(59, 130, 246, 0.05)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#3b82f6',
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    📅 Certificate Validity Period
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>From:</strong> {formatDate(issuanceDate)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>To:</strong> {formatDate(expiryDate)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Duration:</strong> {calculateValidityPeriod()}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Date Validation Errors */}
            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Date Validation Issues:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {validationErrors.map((error, index) => (
                    <li key={index}>
                      <Typography variant="body2">{error}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
          </Box>
        )}

        {/* Step 3: Review & Convert */}
        {currentStep === 2 && (
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Target size={20} color="#10b981" />
              Ready for Conversion
            </Typography>

            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>🎉 All checks passed!</strong> Your certificate is ready
                to be converted to production. This will assign an official COC
                number and make the certificate active.
              </Typography>
            </Alert>

            {/* Final Review */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.6)'
                      : 'rgba(255, 255, 255, 0.8)',
                    border: isDarkMode
                      ? '1px solid rgba(148, 163, 184, 0.1)'
                      : '1px solid rgba(148, 163, 184, 0.2)',
                    borderRadius: 2,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: isDarkMode ? '#f1f5f9' : '#1e293b',
                        mb: 2,
                        fontWeight: 600,
                      }}
                    >
                      📋 Certificate Details
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Current Status:</strong> Draft
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Will Become:</strong> Production Certificate
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>COC Number:</strong> Will be assigned
                      automatically
                    </Typography>
                    <Typography variant="body2">
                      <strong>Items:</strong> {certificate?.Items?.length || 0}{' '}
                      items
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.6)'
                      : 'rgba(255, 255, 255, 0.8)',
                    border: isDarkMode
                      ? '1px solid rgba(148, 163, 184, 0.1)'
                      : '1px solid rgba(148, 163, 184, 0.2)',
                    borderRadius: 2,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: isDarkMode ? '#f1f5f9' : '#1e293b',
                        mb: 2,
                        fontWeight: 600,
                      }}
                    >
                      📅 Validity Period
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Issuance:</strong> {formatDate(issuanceDate)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Expiry:</strong> {formatDate(expiryDate)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Duration:</strong> {calculateValidityPeriod()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Alert severity="warning" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>⚠️ Important:</strong> This action cannot be undone.
                Once converted to production, the certificate cannot be reverted
                to draft status.
              </Typography>
            </Alert>

            {isConverting && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #10b981, #059669)',
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    mt: 1,
                    textAlign: 'center',
                  }}
                >
                  Converting to production...
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box>
            {currentStep > 0 && (
              <CustomButton
                variant="outlined"
                onClick={handleBack}
                disabled={isConverting}
                startIcon={
                  <ArrowRight
                    size={16}
                    style={{ transform: 'rotate(180deg)' }}
                  />
                }
              >
                Back
              </CustomButton>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <CustomButton
              variant="outlined"
              onClick={handleClose}
              disabled={isConverting}
              startIcon={<X size={16} />}
            >
              Cancel
            </CustomButton>

            {currentStep < steps.length - 1 ? (
              <CustomButton
                variant="gradient"
                gradient="primary"
                onClick={handleNext}
                disabled={!canProceed()}
                startIcon={<ArrowRight size={16} />}
                sx={{
                  background:
                    'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
                  '&:hover': {
                    background:
                      'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                    boxShadow: '0 8px 25px rgba(59, 130, 246, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                    transform: 'none',
                  },
                }}
              >
                Next
              </CustomButton>
            ) : (
              <CustomButton
                variant="gradient"
                gradient="success"
                onClick={handleConvert}
                disabled={!canProceed() || isConverting}
                startIcon={
                  isConverting ? (
                    <RefreshCw size={16} className="animate-spin" />
                  ) : (
                    <Save size={16} />
                  )
                }
                sx={{
                  background:
                    'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
                  '&:hover': {
                    background:
                      'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    boxShadow: '0 8px 25px rgba(16, 185, 129, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                    transform: 'none',
                  },
                }}
              >
                {isConverting ? 'Converting...' : 'Convert to Production'}
              </CustomButton>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DraftToProductionModal;
